import { useQuery, useZero } from "@rocicorp/zero/react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { buildResolveScopeName } from "../../../lib/roleChip";
import { MemberDetailModal } from "./MemberDetailModal";

export function MemberDetailModalById({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const z = useZero();
  const [members] = useQuery(queries.allUsers());
  const [personalSpaces] = useQuery(queries.personalSpaceOwners());
  const [allSpaces] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const [allGroups] = useQuery(queries.allUserGroups());
  const [customRoles] = useQuery(queries.allRolesLite());
  const [allApps] = useQuery(queries.connectedAppsLite());
  const [allSkills] = useQuery(queries.skillsLite());
  const [allNetworks] = useQuery(queries.networks());
  const canManageUsers = useHasAccess("tenant:users_manage");
  // Reactivate/deactivate are user-provisioning actions; editing the member's
  // group memberships is a group-membership action, which the IdP only owns when
  // groups are synced — so the two are gated on different concerns.
  const { locked: idpSyncLocked, reason: idpSyncReason } =
    useIdpSyncLock("userProvisioning");
  const { locked: groupMembershipLocked, reason: groupMembershipReason } =
    useIdpSyncLock("groupMembership");

  const member = members.find((m) => m.id === userId);
  const personalSpaceId = personalSpaces.find((space) =>
    space.roleGrantsAsScope.some((grant) => grant.userId === userId)
  )?.id;
  const resolveScopeName = buildResolveScopeName(
    allSpaces,
    allApps,
    allGroups,
    allSkills,
    allNetworks
  );

  if (!member) return null;

  return (
    <MemberDetailModal
      member={member}
      groups={allGroups}
      customRoles={customRoles}
      resolveScopeName={resolveScopeName}
      personalSpaceId={personalSpaceId}
      canManageUsers={canManageUsers}
      idpSyncLocked={idpSyncLocked}
      idpSyncReason={idpSyncReason}
      groupMembershipLocked={groupMembershipLocked}
      groupMembershipReason={groupMembershipReason}
      onClose={onClose}
      onDeactivate={(m) => {
        z.mutate(sharedMutators.deactivateUser({ userId: m.id }));
      }}
      onReactivate={(m) => {
        z.mutate(sharedMutators.reactivateUser({ userId: m.id }));
      }}
      onRemove={(m) => {
        z.mutate(sharedMutators.deleteInvitedUser({ userId: m.id }));
        onClose();
      }}
    />
  );
}
